import {
	getImproveAPI,
	type CommentsControllerSearchCommentsParams,
	type SearchCommentsResponseDto,
	type CreateCommentBodyDto,
	type CreateCommentResponseDto,
	type CommentDto,
	type CommentAuthorDto
} from "../gen";
import {useImproveAxios} from "../../hooks/useImproveAxios";
import {useQuery, useMutation, useQueryClient, useInfiniteQuery} from "@tanstack/react-query";

export const COMMENTS_QUERY_KEYS = {
	COMMENTS: "comments"
} as const;

export type {CommentDto, CommentAuthorDto};

export type SearchCommentsParams = CommentsControllerSearchCommentsParams;
export type SearchCommentsResponse = SearchCommentsResponseDto;
export type CreateCommentParams = CreateCommentBodyDto;

export const useSearchComments = (params: SearchCommentsParams) => {
	const axiosConfig = useImproveAxios();
	const {commentsControllerSearchComments} = getImproveAPI();

	return useQuery<SearchCommentsResponseDto>({
		queryKey: [COMMENTS_QUERY_KEYS.COMMENTS, params],
		queryFn: async () => {
			const response = await commentsControllerSearchComments(params, axiosConfig);
			return response.data;
		},
		enabled: !!params.lessonEnrollmentId,
		staleTime: 60 * 1000
	});
};

export type UseSearchCommentsInfiniteParams = Omit<SearchCommentsParams, "page"> & {enabled?: boolean};

export const useSearchCommentsInfinite = (params: UseSearchCommentsInfiniteParams) => {
	const {enabled: enabledOverride, ...queryParams} = params;
	const axiosConfig = useImproveAxios();
	const {commentsControllerSearchComments} = getImproveAPI();
	const pageSize = params.pageSize ?? 10;

	return useInfiniteQuery<SearchCommentsResponseDto>({
		queryKey: [COMMENTS_QUERY_KEYS.COMMENTS, "infinite", queryParams],
		queryFn: async ({pageParam = 1}) => {
			const response = await commentsControllerSearchComments(
				{...queryParams, page: pageParam as number, pageSize},
				axiosConfig
			);
			return response.data;
		},
		getNextPageParam: (lastPage, allPages) => {
			const loaded = allPages.reduce((sum, p) => sum + p.data.length, 0);
			return lastPage.totalCount > loaded ? allPages.length + 1 : undefined;
		},
		initialPageParam: 1,
		enabled: enabledOverride !== undefined ? enabledOverride : !!params.lessonEnrollmentId
	});
};

export const useCreateComment = () => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const {commentsControllerCreateComment} = getImproveAPI();

	return useMutation<CreateCommentResponseDto, Error, CreateCommentBodyDto>({
		mutationFn: async (body) => {
			const response = await commentsControllerCreateComment(body, axiosConfig);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({queryKey: [COMMENTS_QUERY_KEYS.COMMENTS]});
		}
	});
};
